/**
 * Action Definitions for Admin App
 *
 * These actions represent specific operations users can perform.
 * Actions follow a namespace pattern: "resource:operation"
 *
 * To add a new protected action:
 * 1. Add the action constant here
 * 2. Update the ROLE_PERMISSIONS matrix in permissions.ts
 * 3. Update backend action enforcement (AdminActions + AdminPermissionService)
 * 4. Update docs/cloud/backoffice-app-permissions.md
 */

export const ACTIONS = {
  // Overview actions
  OVERVIEW_VIEW: 'overview:view',

  // Transaction actions
  TRANSACTION_VIEW: 'transaction:view',
  // The aggregated reports, separate from the individual record above.
  REPORT_SALES_VIEW: 'report:sales:view',
  REPORT_ARTICLES_VIEW: 'report:articles:view',
  // The Buchhaltung report (CHG-2026-09-tax-agency-report): its own action, not the manager's —
  // the reader is the controller, and the figures include the internal staff store.
  REPORT_ACCOUNTING_VIEW: 'report:accounting:view',
  // The Bestellvorschlag (CHG-2026-10-bestellvorschlag, D10): viewing the forecast, and entering a
  // Bestellung on it — the second only shows the column, there is no server write behind it.
  REPORT_ORDER_PROPOSAL_VIEW: 'report:order_proposal:view',
  REPORT_ORDER_PROPOSAL_ORDER: 'report:order_proposal:order',

  // Product actions
  PRODUCT_VIEW: 'product:view',
  PRODUCT_CREATE: 'product:create',
  PRODUCT_EDIT: 'product:edit',
  PRODUCT_DELETE: 'product:delete',

  // Assignment actions
  ASSIGNMENT_MANAGE: 'assignment:manage',

  // Price actions
  PRICE_VIEW: 'price:view',
  PRICE_EDIT: 'price:edit',

  // Bundle actions
  BUNDLE_PUBLISH: 'bundle:publish',

  // Edge actions
  EDGE_VIEW: 'edge:view',
  EDGE_PAIRING_CREATE: 'edge:pairing:create',

  // Restaurant actions
  RESTAURANT_VIEW: 'restaurant:view',
  RESTAURANT_MANAGE: 'restaurant:manage',
  RESTAURANT_SCHEDULING_MANAGE: 'restaurant:scheduling:manage',
  RESTAURANT_EDGE_SETTINGS_MANAGE: 'restaurant:edge-settings:manage',
  RESTAURANT_ESL_MANAGE: 'restaurant:esl:manage',
  RESTAURANT_BRANDING_MANAGE: 'restaurant:branding:manage',

  // POS layout actions
  POS_LAYOUT_VIEW: 'pos-layout:view',
  POS_LAYOUT_EDIT: 'pos-layout:edit',

  // Staff actions
  STAFF_VIEW: 'staff:view',
  STAFF_MANAGE: 'staff:manage',

  // Voucher actions
  VOUCHER_VIEW: 'voucher:view',
  VOUCHER_MANAGE: 'voucher:manage',

  // POS void reason actions
  POS_VOID_REASON_VIEW: 'pos-void-reason:view',
  POS_VOID_REASON_MANAGE: 'pos-void-reason:manage',

  // Entnahme reason actions
  ENTNAHME_REASON_VIEW: 'entnahme-reason:view',
  ENTNAHME_REASON_MANAGE: 'entnahme-reason:manage',

  // Inventory actions
  INVENTORY_VIEW: 'inventory:view',
  INVENTORY_RECEIVE: 'inventory:receive',
  INVENTORY_COUNT: 'inventory:count',
  INVENTORY_ADJUST: 'inventory:adjust',
  INVENTORY_TRANSFER: 'inventory:transfer',
  INVENTORY_COUNT_SESSION: 'inventory:count_session',
  INVENTORY_DELIVERY: 'inventory:delivery',

  // Purchase orders to the wholesaler (CHG-2026-10-edi-purchase-orders, D13): the Bestellungen
  // page, and creating, editing and sending orders.
  PURCHASE_ORDER_VIEW: 'purchase_order:view',
  PURCHASE_ORDER_ORDER: 'purchase_order:order',

  // Import actions
  CATALOG_IMPORT: 'catalog:import',
  PRICING_IMPORT: 'pricing:import',

  // Category actions
  CATEGORY_VIEW: 'category:view',
  CATEGORY_CREATE: 'category:create',

  // Menu actions
  MENU_VIEW: 'menu:view',
  MENU_CREATE: 'menu:create',
  MENU_EDIT: 'menu:edit',
  MENU_DELETE: 'menu:delete',
  MENU_PUBLISH: 'menu:publish',

  // User and role actions
  USER_MANAGE: 'user:manage',
  ROLE_PERMISSION_MANAGE: 'role:permissions:manage',

  // Customer actions
  CUSTOMER_VIEW: 'customer:view',
  CUSTOMER_MANAGE: 'customer:manage',

  // Integrations: API clients for machine callers (tenant admins only)
  INTEGRATIONS_MANAGE: 'integrations:manage',
} as const

export type Action = (typeof ACTIONS)[keyof typeof ACTIONS]
